// MQTT 编排层：全应用单条共享连接 + 订阅者并集管理，对外作为 $mqtt 单例 [Claude Code]
// 模型照搬 wsSubscriptionCenter：以订阅者(subscriberId)为真相源，全局订阅 = 所有订阅者 pid 的并集；
// 只建一次连接，后续 pid 增减走 updateTopic 增订 / 本地 unsubscribe 退订，订阅者清空才断连 [Claude Code]
import mqttClient from '@/assets/js/mqttClient'
import { getMqttToken, mqttReportRule, updateMqttSubscribeTopics } from '@/axios/mqtt'

const DEFAULT_RENEW_INTERVAL = 60 * 60 * 1000 // 默认续期周期 1 小时 [Claude Code]

class MqttManager {
  constructor () {
    this.client = mqttClient // 持有纯客户端单例 [Claude Code]
    this.renewTimer = null // 续期定时器句柄 [Claude Code]
    this._renewActive = false // 续期循环是否激活（disconnect 后置 false）[Claude Code]
    // 连接状态机：保证全应用只建一次连接 + 建连单飞 [Claude Code]
    this._connState = 'idle' // idle | connecting | connected
    this._connectingPromise = null // 建连进行中的单飞 Promise [Claude Code]
    this._connResult = null // 建连成功的 token result（host/clientId/subTopics...）[Claude Code]
    // 订阅者表：subscriberId(组件 _uid) -> Set<pid>，全局要订阅的 pid = 各订阅者并集 [Claude Code]
    this._subscribers = new Map()
    // 当前底层实际已订阅的 topic 全集（即「全局 topic list」）[Claude Code]
    this._subscribedTopics = new Set()
    // 每个 pid 贡献的 topic 列表，退订时据此精确还原 [Claude Code]
    this._pidTopics = new Map()
    // reconcile 串行化链：避免并发 reconcile 对同一新 pid 重复 updateTopic [Claude Code]
    this._reconcileChain = Promise.resolve()
  }

  // 底层建连：申请 token → 连接 → 订阅 subTopics → 启动自动续期；失败 resolve(null)
  // 保留作为底层实现，供 ensureConnected 复用 / 回滚；不对外直接当订阅入口 [Claude Code]
  async connectWithToken (processId, ruleOptions = {}) {
    // MH-3054：getMqttToken 后端必填 processId，为空直接按建连失败语义返回，不发请求 [Claude Code]
    if (!processId) return null
    let res
    try {
      res = await getMqttToken({ processId })
    } catch (err) {
      // 网络/超时失败：吞掉 reject，避免 UnhandledRejection [Claude Code]
      console.warn('[MQTT] getMqttToken request failed:', err)
      return null
    }
    // 业务失败：非 0x0 不连接 [Claude Code]
    if (!res || res.errorCode !== '0x0' || !res.result) {
      console.warn('[MQTT] getMqttToken business error:', res)
      return null
    }

    const result = res.result
    const url = `wss://${result.host}:${result.wsPort}/mqtt`
    try {
      await this.client.connect({
        url,
        clientId: result.clientId,
        username: result.username,
        password: result.token,
        clean: true,
        protocolVersion: 5
      })
    } catch (err) {
      // 连接失败同样吞掉，保持"失败 resolve(null)"语义、杜绝 UnhandledRejection [Claude Code]
      console.warn('[MQTT] client.connect failed:', err)
      return null
    }

    // 逐个订阅 token 返回的 subTopics（首个 pid 的 topic），单个失败仅告警不中断 [Claude Code]
    if (Array.isArray(result.subTopics)) {
      for (const topic of result.subTopics) {
        try {
          await this.client.subscribe(topic, { qos: 0 })
        } catch (err) {
          console.warn(`[MQTT] subscribe failed: ${topic}`, err)
        }
      }
    }

    // 启动自动续期；ruleOptions.autoRenew === false 时跳过（续期接口未就绪场景）[Claude Code]
    if (ruleOptions.autoRenew !== false) {
      this.startRenew(processId, ruleOptions)
    }
    return result
  }

  // 自动续期：无论 reportRule 成败都安排下次，唯一中止条件是 disconnect() 置 _renewActive=false [Claude Code]
  startRenew (processId, ruleOptions = {}) {
    // MH-3054：reportRule 的 URL 以 processId 拼段，为空不启动续期循环，避免发出非法请求 [Claude Code]
    if (!processId) return
    if (this.renewTimer) {
      clearTimeout(this.renewTimer)
      this.renewTimer = null
    }
    this._renewActive = true // 标记续期循环为激活态 [Claude Code]
    const intervalMs = ruleOptions.intervalMs || DEFAULT_RENEW_INTERVAL

    const renew = async () => {
      const rule = {
        metricType: 'frame',
        reportIntervalMs: 1000,
        collectIntervalMs: 1000,
        effectiveUntil: Date.now() + intervalMs,
        ...(ruleOptions.rule || {})
      }
      try {
        const res = await mqttReportRule({ processId, rule })
        if (!res || res.code !== 0) {
          console.warn('[MQTT] reportRule business error:', res)
        }
      } catch (err) {
        // 504 等失败：吞掉，下个周期照常重试 [Claude Code]
        console.warn('[MQTT] reportRule request failed, will retry next cycle:', err)
      }
      // async body 跑完后再检查是否已 disconnect，避免在 disconnect 后继续注册 [Claude Code]
      if (this._renewActive) {
        this.renewTimer = setTimeout(renew, intervalMs)
      }
    }
    renew()
  }

  // 幂等建连（单飞）：connected 复用、connecting 复用 in-flight Promise、idle 才真正建连 [Claude Code]
  ensureConnected (processId, opts = {}) {
    if (this._connState === 'connected') return Promise.resolve(this._connResult)
    if (this._connState === 'connecting') return this._connectingPromise
    this._connState = 'connecting'
    this._connectingPromise = (async () => {
      const result = await this.connectWithToken(processId, opts)
      if (!result) {
        // 建连失败：回 idle 清标记，下次可重试 [Claude Code]
        this._connState = 'idle'
        this._connectingPromise = null
        return null
      }
      // 首个 pid 的 subTopics 已在 connectWithToken 内订阅，这里只登记账本 [Claude Code]
      const topics = Array.isArray(result.subTopics) ? result.subTopics : []
      this._pidTopics.set(processId, topics.slice())
      topics.forEach(t => this._subscribedTopics.add(t))
      this._connState = 'connected'
      this._connResult = result
      return result
    })()
    return this._connectingPromise
  }

  // 所有订阅者声明的 pid 并集（去重）[Claude Code]
  _getMergedPids () {
    const set = new Set()
    this._subscribers.forEach(pids => pids.forEach(pid => set.add(pid)))
    return [...set]
  }

  // 注册/更新订阅者声明的 pid 列表 → 确保连接 → 收敛订阅 [Claude Code]
  async subscribe (subscriberId, pids, opts = {}) {
    if (!subscriberId) return
    this._subscribers.set(subscriberId, new Set((pids || []).filter(Boolean)))
    const merged = this._getMergedPids()
    if (!merged.length) return // 无任何 pid 需求 [Claude Code]
    await this.ensureConnected(merged[0], opts)
    if (this._connState !== 'connected') return // 建连失败，留待下次 [Claude Code]
    await this._reconcile()
  }

  // updatePids 与 subscribe 同义（覆盖该订阅者的 pid 集合后收敛）[Claude Code]
  updatePids (subscriberId, pids, opts = {}) {
    return this.subscribe(subscriberId, pids, opts)
  }

  // 移除订阅者 → 按剩余并集收敛（退掉无人需要的 topic）→ 订阅者清空则断连 [Claude Code]
  async unsubscribe (subscriberId) {
    if (!this._subscribers.has(subscriberId)) return // 未知订阅者：幂等无副作用 [Claude Code]
    this._subscribers.delete(subscriberId)
    if (this._connState === 'connected') {
      await this._reconcile()
    }
    if (this._subscribers.size === 0) {
      this.disconnect()
    }
  }

  // 串行化收敛入口：把多次 reconcile 排进同一条 Promise 链，避免并发重复 updateTopic [Claude Code]
  _reconcile () {
    this._reconcileChain = this._reconcileChain
      .then(() => this._doReconcile())
      .catch(err => console.warn('[MQTT] reconcile error:', err))
    return this._reconcileChain
  }

  // 收敛：merged 并集 vs 已订阅集合求差——退掉多余、增订缺失（幂等）[Claude Code]
  async _doReconcile () {
    if (this._connState !== 'connected') return
    const merged = new Set(this._getMergedPids())

    // 1) 退订：已订阅但并集已不含的 pid —— 仅本地 unsubscribe，不通知后端 [Claude Code]
    for (const pid of [...this._pidTopics.keys()]) {
      if (merged.has(pid)) continue
      const topics = this._pidTopics.get(pid) || []
      for (const topic of topics) {
        try {
          await this.client.unsubscribe(topic)
        } catch (err) {
          console.warn(`[MQTT] unsubscribe failed: ${topic}`, err)
        }
        this._subscribedTopics.delete(topic)
      }
      this._pidTopics.delete(pid)
    }

    // 2) 增订：并集需要但尚未订阅的 pid —— 走 updateTopic(add) 拿全集后订阅新增 topic [Claude Code]
    for (const pid of merged) {
      // MH-3054：updateTopic 后端必填 processId，空 pid 跳过不增订 [Claude Code]
      if (!pid) continue
      if (this._pidTopics.has(pid)) continue
      let res
      try {
        res = await updateMqttSubscribeTopics({ processIds: [pid], mode: 'add' })
      } catch (err) {
        // 接口失败：告警 + 不阻断（该 pid 留待下次 reconcile 重试）[Claude Code]
        console.warn('[MQTT] updateTopic request failed:', err)
        continue
      }
      if (!res || res.errorCode !== '0x0' || !res.result) {
        console.warn('[MQTT] updateTopic business error:', res)
        continue
      }
      const sub = Array.isArray(res.result.sub) ? res.result.sub : []
      // 本地订阅响应里尚未订阅的 topic，保持与后端全集一致 [Claude Code]
      for (const topic of sub) {
        if (this._subscribedTopics.has(topic)) continue
        try {
          await this.client.subscribe(topic, { qos: 0 })
        } catch (err) {
          console.warn(`[MQTT] subscribe failed: ${topic}`, err)
        }
        this._subscribedTopics.add(topic)
      }
      // 按 topic 内嵌的 pid 精确归属（格式 media/metrics/{pid}/frame/+）[Claude Code]
      this._pidTopics.set(pid, sub.filter(topic => topic.indexOf(`/${pid}/`) !== -1))
    }
  }

  // 以下透传给内部 client [Claude Code]
  onMessage (cb) { return this.client.onMessage(cb) }
  offMessage (cb) { return this.client.offMessage(cb) }
  publish (topic, msg, opts) { return this.client.publish(topic, msg, opts) }
  getConnected () { return this.client.getConnected() }

  // 断开：停续期 + 断底层连接 + 复位状态机与订阅账本（闭环清理）[Claude Code]
  disconnect () {
    this._renewActive = false // 先置 false，阻止 async renew body 跑完后继续注册 [Claude Code]
    if (this.renewTimer) {
      clearTimeout(this.renewTimer)
      this.renewTimer = null
    }
    this.client.disconnect()
    // 复位状态机与账本，下次 subscribe 可重新建连 [Claude Code]
    this._connState = 'idle'
    this._connectingPromise = null
    this._connResult = null
    this._subscribedTopics.clear()
    this._pidTopics.clear()
    this._subscribers.clear()
  }
}

// 单例：对外作为 $mqtt [Claude Code]
const mqttManager = new MqttManager()
export default mqttManager
